// ─── ETF glossary terms ───────────────────────────────────────────────
// Ported verbatim from dane/frontend/app/etf-website/data/glossary.ts.

interface GlossaryTerm {
  term: string;
  headline: string;
  definition: string;
  categories: string[];
  abbreviation?: string;
  index: string;
  formula?: string;
  formulaLabel?: string;
  related?: { label: string; id: string }[];
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  // A
  {
    term: "Average Coupon",
    headline: "The average interest rate paid by the bonds a fund holds.",
    definition:
      "The average interest rate paid by the bonds in the fund, weighted by how much of each bond the fund holds.",
    categories: ["Income"],
    index: "#066",
  },
  {
    term: "Authorized Participant",
    headline:
      "A financial institution that may create and redeem ETF shares directly with the fund.",
    definition:
      "Authorized Participants, or APs, are typically large financial institutions, often broker-dealers, that have agreements allowing them to create and redeem ETF shares directly with the fund in large blocks. APs participate in the ETF creation and redemption process and may help reduce differences between an ETF’s market price and its net asset value, or NAV.",
    categories: ["Structure", "Basics"],
    index: "#001",
    related: [
      { label: "NAV", id: "term-net-asset-value" },
      { label: "Creation / Redemption", id: "term-creation--redemption" },
      { label: "Premium / Discount", id: "term-premium--discount-history" },
    ],
  },
  {
    term: "Assets Under Management",
    headline: "The total market value of a fund’s assets.",
    definition:
      "Assets Under Management, or AUM, refers to the total value of the assets held by a fund. It is commonly used as a measure of fund size. AUM does not by itself determine an ETF’s trading liquidity.",
    categories: ["Basics"],
    index: "#002",
  },
  {
    term: "Average Daily Volume",
    headline: "A measure of a security’s historical trading activity.",
    definition:
      "Average Daily Volume, or ADV, is the average number of shares traded per day over a specified period. ADV reflects historical secondary-market trading activity. An ETF’s overall liquidity may also be influenced by the liquidity of its underlying holdings.",
    categories: ["Trading"],
    index: "#003",
    related: [
      { label: "Liquidity", id: "term-liquidity" },
      { label: "Bid-Ask Spread", id: "term-bid-ask-spread" },
    ],
  },
  // B
  {
    term: "Basis Point",
    headline:
      "One one-hundredth of a percentage point, commonly used to express changes in rates and fees.",
    definition:
      "A basis point, abbreviated as bps, is equal to 0.01%, or one one-hundredth of one percentage point. Basis points are commonly used in finance to describe changes in interest rates, bond yields, expense ratios, and other metrics expressed as percentages. For example, a change from 0.20% to 0.45% is a change of 25 basis points.",
    categories: ["Basics", "Costs"],
    abbreviation: "BPS",
    index: "#040",
    formula:
      "1 basis point = 0.01% = 0.0001 in decimal form · 100 basis points = 1 percentage point",
    formulaLabel: "Formula",
    related: [
      { label: "Expense Ratio", id: "term-expense-ratio" },
      { label: "Duration", id: "term-duration" },
    ],
  },
  {
    term: "Bid-Ask Spread",
    headline: "The difference between the highest bid price and the lowest ask price.",
    definition:
      "The bid is the highest price a buyer is willing to pay for shares, and the ask is the lowest price a seller is willing to accept. The difference between those two prices is the bid-ask spread. Wider spreads generally indicate higher transaction costs. Spreads may change based on market conditions, trading volume, and the liquidity of the ETF’s underlying holdings.",
    categories: ["Trading", "Costs"],
    index: "#004",
    formula: "Spread (%) = (Ask − Bid) ÷ Midpoint × 100",
    formulaLabel: "Formula",
    related: [
      { label: "Median Bid-Ask Spread", id: "term-median-bid-ask-spread" },
      { label: "Liquidity", id: "term-liquidity" },
    ],
  },
  {
    term: "Buffer",
    headline: "A stated level of downside protection over a specified outcome period.",
    definition:
      "A buffer is a feature of certain defined outcome ETFs that is designed to reduce the effect of losses in a reference asset over a stated outcome period, up to a specified percentage. For example, a 15% buffer is generally designed to absorb the first 15% of losses of the reference asset during the outcome period, before losses are borne by the fund. A buffer is generally achieved through the use of options and is typically accompanied by a limit on upside returns.",
    categories: ["Structure", "Risk"],
    index: "#005",
    related: [
      { label: "Cap Rate", id: "term-cap-rate" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  // C
  {
    term: "Credit Quality",
    headline: "The mix of credit ratings across a fund’s bond holdings.",
    definition:
      "Credit quality shows the mix of credit ratings across the fund's bonds, reflecting the average ability of issuers to repay their debt.",
    categories: ["Risk", "Basics"],
    index: "#067",
    related: [{ label: "Credit Rating", id: "term-credit-rating" }],
  },
  {
    term: "Credit Rating",
    headline: "A measure of a bond issuer’s ability to repay its debt.",
    definition:
      "Credit ratings measure an issuer's ability to repay its debt, from highest to lowest: AAA, AA, A, BBB (investment grade), then BB, B, CCC, CC, C, and D (below investment grade). U.S. Treasury obligations are backed by the U.S. government.",
    categories: ["Risk", "Basics"],
    index: "#068",
    related: [{ label: "Credit Quality", id: "term-credit-quality" }],
  },
  {
    term: "Compounding",
    headline:
      "The process by which gains or losses in one period affect returns in future periods.",
    definition:
      "Compounding applies naturally to all investments: returns earned in one period are reinvested and become the base for future returns. In products that reset their exposure daily — such as leveraged or inverse ETFs — compounding can cause multi-period returns to differ materially from what investors might expect by simply multiplying the stated daily return. In volatile markets, this divergence can be significant. Understanding compounding is essential before holding any daily-reset product for more than one trading session.",
    categories: ["Advanced", "Basics"],
    index: "#006",
    related: [
      { label: "Daily Reset", id: "term-daily-reset" },
      { label: "Path Dependency", id: "term-path-dependency" },
    ],
  },
  {
    term: "Creation Unit",
    headline:
      "The standardized block size at which ETF shares are created or redeemed at the institutional level.",
    definition:
      "A Creation Unit is a large block of ETF shares that can be created or redeemed directly with the fund’s issuer by eligible institutional counterparties, typically Authorized Participants. The creation-unit mechanism is central to how ETFs maintain pricing discipline — it allows APs to arbitrage away premiums and discounts by assembling or disassembling the underlying basket in exchange for ETF shares at NAV.",
    categories: ["Structure", "Basics"],
    index: "#007",
    related: [
      { label: "Authorized Participant", id: "term-authorized-participant" },
      {
        label: "Creation / Redemption",
        id: "term-creation--redemption",
      },
      { label: "NAV", id: "term-net-asset-value" },
    ],
  },
  {
    term: "Creation / Redemption",
    headline: "The process by which ETF shares are issued and removed from the market.",
    definition:
      "In the creation and redemption process, an Authorized Participant delivers a specified basket of securities, cash, or both to the fund in exchange for a block of ETF shares, or returns a block of ETF shares to the fund in exchange for a basket of securities, cash, or both. This process generally takes place in large blocks called Creation Units. Many ETFs use in-kind transactions for creations and redemptions.",
    categories: ["Structure", "Basics"],
    index: "#008",
    related: [
      { label: "Authorized Participant", id: "term-authorized-participant" },
      { label: "NAV", id: "term-net-asset-value" },
    ],
  },
  {
    term: "Cap Rate",
    headline: "The maximum gain a buffer ETF can deliver in a given outcome period.",
    definition:
      "If the S&P 500 returns 30% but the cap is 15%, the fund returns 15%. The cap exists because the fund sells call options to finance put options (which create the buffer). Higher buffers = lower caps, all else equal. Cap rates reset each outcome period and vary with options pricing and market volatility.",
    categories: ["Structure", "Risk"],
    index: "#009",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Capital Expenditure (CapEx)",
    headline:
      "Spending on long-lived physical assets such as data centers, hardware, and infrastructure.",
    definition:
      "Capital expenditure, or CapEx, refers to funds a company spends to acquire, upgrade, or maintain physical assets — including buildings, equipment, data centers, and specialized hardware.",
    categories: ["Basics"],
    index: "#039",
  },
  {
    term: "Current Price",
    headline: "The closing price of a fund’s shares as of a given date.",
    definition: "Price as of date’s close.",
    categories: ["Basics", "Trading"],
    index: "#045",
    related: [
      { label: "Market Price", id: "term-market-price" },
      { label: "NAV", id: "term-net-asset-value" },
    ],
  },
  // D
  {
    term: "Distribution NAV",
    headline: "The fund’s NAV per share on the date a distribution is declared.",
    definition: "The fund's net asset value per share on the date the distribution was declared.",
    categories: ["Income", "Basics"],
    index: "#069",
    related: [{ label: "NAV", id: "term-net-asset-value" }],
  },
  {
    term: "Dividend Income",
    headline: "Income from interest and dividends paid out to shareholders.",
    definition:
      "Income from the interest and dividends earned by the fund's holdings and paid out to shareholders.",
    categories: ["Income"],
    index: "#070",
  },
  {
    term: "Discount to NAV",
    headline: "When an ETF’s market price is lower than its net asset value.",
    definition:
      "A discount occurs when an ETF’s market price is below its NAV. Discounts may occur for a variety of reasons, including market volatility, trading conditions, or differences between the trading hours of the ETF and its underlying holdings.",
    categories: ["Trading", "Basics"],
    index: "#010",
    formula:
      "Discount (%) = (Market Price − NAV) ÷ NAV × 100 · Negative result = discount · Positive = premium",
    formulaLabel: "Formula",
    related: [
      { label: "Premium to NAV", id: "term-premium-to-nav" },
      { label: "NAV", id: "term-net-asset-value" },
    ],
  },
  {
    term: "Duration",
    headline: "A measure of a fixed-income portfolio’s sensitivity to interest rate changes.",
    definition:
      "Duration estimates how much the value of a bond or bond fund may change in response to changes in interest rates. In general, higher duration indicates greater sensitivity to interest rate changes. For example, a duration of 5 years generally indicates that the value of the portfolio may change by approximately 5% for a 1% change in interest rates, all else equal. Duration is different from maturity.",
    categories: ["Risk", "Income"],
    index: "#011",
  },
  {
    term: "Daily Reset",
    headline:
      "A structure in which leveraged exposure is recalibrated at the end of each trading day.",
    definition:
      "A daily-reset strategy is designed to deliver its stated multiple or inverse result relative to the reference asset for one trading day at a time. At the end of each day, exposure is reset to match the stated leverage ratio going into the next session. Over periods longer than one day, returns can diverge substantially from simply multiplying the cumulative return of the reference asset — particularly in volatile or directionless markets. These products are designed for sophisticated investors with short holding horizons.",
    categories: ["Structure", "Advanced"],
    index: "#012",
    related: [
      { label: "Compounding", id: "term-compounding" },
      { label: "Path Dependency", id: "term-path-dependency" },
    ],
  },
  {
    term: "Distribution Yield",
    headline: "A yield measure based on distributions paid over a stated period.",
    definition:
      "Distribution yield is generally calculated by annualizing distributions paid by a fund over a stated period, often the prior 12 months, and dividing that amount by the fund’s current NAV or market price, depending on the methodology used. Distribution yield reflects past distributions and may include amounts that do not represent net investment income, such as return of capital.",
    categories: ["Income"],
    index: "#013",
    related: [
      { label: "30-Day SEC Yield", id: "term-30-day-sec-yield" },
      { label: "Yield to Worst", id: "term-yield-to-worst" },
    ],
  },
  {
    term: "Downside Before Buffer",
    headline:
      "The distance the reference asset must decline before the buffer begins absorbing losses.",
    definition:
      "Downside before buffer indicates how far the reference asset needs to fall from the start of the outcome period before the buffer protection begins to apply. When the reference asset return is positive during the outcome period, this value is generally 0% because the reference asset has not entered negative territory. When the reference asset is negative but within the buffer range, this value reflects how much further it would need to fall before losses pass through to the fund.",
    categories: ["Structure", "Risk"],
    index: "#042",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Days Remaining",
    headline: "The number of days until the current outcome period’s option structure expires.",
    definition: "Amount of time until the option structure expires.",
    categories: ["Structure"],
    index: "#046",
    related: [
      { label: "Outcome Period", id: "term-outcome-period" },
      { label: "Remaining Outcome Period", id: "term-remaining-outcome-period" },
    ],
  },
  {
    term: "Deep Buffer",
    headline:
      "A buffer that begins absorbing losses only after the reference asset has declined past an initial threshold.",
    definition:
      "A deep buffer is a type of defined outcome strategy in which the downside protection does not start at zero. Instead, the investor bears the first portion of losses, and the buffer absorbs losses within a specified range beyond that point. For example, a 30% deep buffer on SPY may be designed to absorb losses between -5% and -35% over the outcome period, meaning the investor is exposed to the first 5% of downside and any losses beyond -35%. Deep buffers generally offer a wider range of protection than standard buffers, but at the cost of absorbing some initial loss.",
    categories: ["Structure", "Risk"],
    index: "#064",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Downside Before Buffer", id: "term-downside-before-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Derivatives",
    headline:
      "A financial contract whose value fluctuates based on the price of one or more underlying assets.",
    definition:
      "A derivative is a kind of financial contract between two or more parties, the value of which fluctuates based on the price of one or more underlying assets. Traders can purchase these agreements and utilize them to hedge against risk, speculate on the asset’s movement, or leverage a position.",
    categories: ["Basics", "Advanced"],
    index: "#047",
    related: [
      { label: "FLEX Options", id: "term-flex-options" },
      { label: "Buffer", id: "term-buffer" },
    ],
  },
  // E
  {
    term: "Effective Duration",
    headline: "How sensitive a fund’s price is to changes in interest rates.",
    definition:
      "Effective duration is a measure of how sensitive the fund's price is to changes in interest rates. A duration of 2 years means the price would be expected to move about 2% for every 1% change in rates.",
    categories: ["Risk", "Income"],
    index: "#071",
    related: [{ label: "Duration", id: "term-duration" }],
  },
  {
    term: "Effective Maturity",
    headline: "The expected average time until a fund’s bonds are repaid.",
    definition:
      "The expected average time until the fund's bonds are repaid, adjusted for features like early redemption or prepayment.",
    categories: ["Income", "Risk"],
    index: "#072",
    related: [{ label: "Weighted Avg Maturity", id: "term-weighted-avg-maturity" }],
  },
  {
    term: "Expense Ratio",
    headline: "The annual operating fee charged by the fund.",
    definition:
      "The expense ratio is the annual rate of a fund’s operating expenses, expressed as a percentage of average net assets. Fund expenses are reflected in the fund’s NAV.",
    categories: ["Costs", "Basics"],
    index: "#014",
  },
  {
    term: "EEM Index Reference Price",
    headline: "The MSCI Emerging Markets Index level used to set a fund’s structured outcomes.",
    definition:
      "The MSCI Emerging Markets Index that we will set the structured outcomes on. This is the price that the buffer starts on.",
    categories: ["Structure"],
    index: "#059",
    related: [
      {
        label: "iShares MSCI Emerging Markets (EEM)",
        id: "term-ishares-msci-emerging-markets",
      },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "EFA Index Reference Price",
    headline: "The MSCI EAFE Index level used to set a fund’s structured outcomes.",
    definition:
      "The MSCI EAFE Index that we will set the structured outcomes on. This is the price that the buffer starts on.",
    categories: ["Structure"],
    index: "#060",
    related: [
      { label: "iShares MSCI EAFE (EFA)", id: "term-ishares-msci-eafe" },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  // F
  {
    term: "FLEX Options",
    headline:
      "Exchange-listed options with customizable terms, commonly used in defined-outcome strategies.",
    definition:
      "FLEX Options are exchange-listed options contracts with flexible terms, including customizable expiration dates, strike prices, and exercise styles. Unlike standard listed options, they can be tailored more precisely to match a fund’s specific outcome-period requirements. They are frequently used in buffer and defined-outcome ETF structures because they allow portfolio managers to engineer specific combinations of downside protection and upside participation that would be difficult to replicate with standard-term options.",
    categories: ["Structure", "Advanced"],
    index: "#015",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Cap Rate", id: "term-cap-rate" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "FTSE",
    headline:
      "A global index provider whose fixed income benchmarks are tracked by certain bond funds.",
    definition:
      "FTSE (administered by FTSE Fixed Income LLC) is a provider of market indexes, including the U.S. Treasury, Treasury Bill, investment-grade corporate, and high-yield bond indexes tracked by certain funds. A fund is not sponsored, endorsed, sold, or promoted by FTSE Fixed Income LLC or its affiliates, and FTSE Fixed Income LLC makes no representation regarding the advisability of investing in the fund or the ability of an index to track general market performance. An investment cannot be made directly in an index or average. All indexes and averages are unmanaged.",
    categories: ["Basics"],
    index: "#065",
    related: [{ label: "Reference Asset", id: "term-reference-asset" }],
  },
  // G
  {
    term: "Geographic Allocation",
    headline: "The breakdown of a fund’s bonds by issuer country.",
    definition:
      "Shows the breakdown of the fund's bonds by the country where each issuer is based.",
    categories: ["Basics"],
    index: "#073",
  },
  // H
  {
    term: "High Yield Corporate Bond",
    headline:
      "A corporate bond rated below investment grade (BB+ or lower) that pays a higher coupon to compensate for greater credit risk.",
    definition:
      "A corporate bond rated below investment grade (BB+ or lower) that pays a higher coupon to compensate for greater credit risk.",
    categories: ["Basics"],
    index: "#080",
  },
  // I
  {
    term: "Industry Allocation",
    headline: "How a fund’s corporate bonds are divided across business sectors.",
    definition:
      "Shows how the fund's corporate bonds are divided across different business sectors.",
    categories: ["Basics"],
    index: "#074",
  },
  {
    term: "Investment Grade Corporate Bond",
    headline:
      "A corporate bond rated BBB- / Baa3 or higher, indicating relatively low credit risk and a strong likelihood of repayment.",
    definition:
      "A corporate bond rated BBB- / Baa3 or higher, indicating relatively low credit risk and a strong likelihood of repayment.",
    categories: ["Basics"],
    index: "#081",
  },
  {
    term: "Intraday NAV",
    headline: "An estimated value of an ETF’s holdings published during trading hours.",
    definition:
      "Intraday NAV, sometimes referred to as iNAV or intraday indicative value, is an estimate of a fund’s value during the trading day based on available market information. Official NAV is generally calculated once each business day after market close. For ETFs that hold securities trading in markets that are closed during U.S. trading hours, intraday estimates may be less reflective of current portfolio value.",
    categories: ["Trading", "Basics"],
    index: "#016",
    related: [
      { label: "NAV", id: "term-net-asset-value" },
      { label: "Premium", id: "term-premium-to-nav" },
      { label: "Discount", id: "term-discount-to-nav" },
    ],
  },
  {
    term: "iShares MSCI EAFE (EFA)",
    headline:
      "An ETF providing exposure to large- and mid-cap developed market equities outside the US and Canada.",
    definition:
      "The iShares MSCI EAFE ETF (EFA) seeks to track the investment results of the MSCI EAFE Index, which measures the equity market performance of developed markets outside of the U.S. and Canada. The Fund is not affiliated with, sponsored, endorsed, sold or promoted by iShares® or its affiliates. An investment cannot be made directly in an index or average. All indexes and averages are unmanaged.",
    categories: ["Basics"],
    index: "#056",
    related: [
      { label: "Reference Asset", id: "term-reference-asset" },
      { label: "EFA Index Reference Price", id: "term-efa-index-reference-price" },
    ],
  },
  {
    term: "iShares MSCI Emerging Markets (EEM)",
    headline: "An ETF providing exposure to large- and mid-cap emerging market equities.",
    definition:
      "The iShares MSCI Emerging Markets ETF (EEM) seeks to track the investment results of the MSCI Emerging Markets Index, which measures equity market performance in global emerging markets. The Fund is not affiliated with, sponsored, endorsed, sold or promoted by iShares® or its affiliates. An investment cannot be made directly in an index or average. All indexes and averages are unmanaged.",
    categories: ["Basics"],
    index: "#057",
    related: [
      { label: "Reference Asset", id: "term-reference-asset" },
      { label: "EEM Index Reference Price", id: "term-eem-index-reference-price" },
    ],
  },
  {
    term: "iShares Russell 2000 (IWM)",
    headline: "An ETF providing exposure to US small-cap equities.",
    definition:
      "The iShares Russell 2000 ETF (IWM) seeks to track the investment results of the Russell 2000® Index, which measures the performance of the small-cap segment of the U.S. equity market. The Fund is not affiliated with, sponsored, endorsed, sold or promoted by iShares® or its affiliates. An investment cannot be made directly in an index or average. All indexes and averages are unmanaged.",
    categories: ["Basics"],
    index: "#058",
    related: [
      { label: "Reference Asset", id: "term-reference-asset" },
      { label: "IWM Index Reference Price", id: "term-iwm-index-reference-price" },
    ],
  },
  {
    term: "IWM Index Reference Price",
    headline: "The Russell 2000 Index level used to set a fund’s structured outcomes.",
    definition:
      "The Russell 2000 Index that we will set the structured outcomes on. This is the price that the buffer starts on.",
    categories: ["Structure"],
    index: "#061",
    related: [
      { label: "iShares Russell 2000 (IWM)", id: "term-ishares-russell-2000" },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  // L
  {
    term: "Long-Term Capital Gain",
    headline: "Profit from selling investments held for more than one year.",
    definition:
      "Long-term capital gains are profits from selling investments held for more than one year, passed on to shareholders.",
    categories: ["Income"],
    index: "#075",
    related: [{ label: "Short-Term Capital Gain", id: "term-short-term-capital-gain" }],
  },
  {
    term: "Liquidity",
    headline: "How easily you can buy or sell without moving the price against yourself.",
    definition:
      "ETF liquidity has two layers: secondary market liquidity (how actively the ETF itself trades, reflected in bid-ask spread) and underlying liquidity (how liquid the holdings are). An ETF can appear liquid on the surface but face challenges if its holdings trade infrequently — this matters in stress scenarios when APs need to create or redeem large blocks.",
    categories: ["Trading", "Risk"],
    index: "#017",
    related: [
      { label: "Bid-Ask Spread", id: "term-bid-ask-spread" },
      { label: "Median Bid-Ask Spread", id: "term-median-bid-ask-spread" },
    ],
  },
  {
    term: "Lead Market Maker",
    headline: "The primary market-making firm assigned to support trading in a particular ETF.",
    definition:
      "A Lead Market Maker is the designated trading firm responsible for maintaining active two-sided quotes for an ETF on an exchange. Unlike general market makers who may quote many securities opportunistically, an LMM has a formal arrangement with the exchange or fund issuer to help ensure orderly trading. The LMM plays a key role in supporting liquidity, especially during the early life of a fund or in times of market stress, and can help maintain tighter spreads and more continuous price discovery.",
    categories: ["Trading", "Structure"],
    index: "#018",
    related: [
      { label: "Market Maker", id: "term-market-maker" },
      { label: "Bid-Ask Spread", id: "term-bid-ask-spread" },
      { label: "Liquidity", id: "term-liquidity" },
    ],
  },
  // M
  {
    term: "Maturity Distribution",
    headline: "How a fund’s bonds are spread across time-to-maturity ranges.",
    definition:
      "Shows how the fund's bonds are spread across different ranges of time remaining until they come due.",
    categories: ["Income"],
    index: "#076",
    related: [
      { label: "Weighted Avg Maturity", id: "term-weighted-avg-maturity" },
      { label: "Effective Maturity", id: "term-effective-maturity" },
    ],
  },
  {
    term: "Median Bid-Ask Spread",
    headline: "A historical measure of typical bid-ask spreads over a stated period.",
    definition:
      "The median bid-ask spread is the midpoint value of quoted bid-ask spreads observed over a stated period, often 30 calendar days. It is intended to provide a standardized measure of an ETF’s typical trading spread over that period.",
    categories: ["Trading", "Costs"],
    index: "#019",
    related: [
      { label: "Bid-Ask Spread", id: "term-bid-ask-spread" },
      { label: "Liquidity", id: "term-liquidity" },
    ],
  },
  {
    term: "Market Maker",
    headline: "A trading firm that continuously posts bids and offers for an ETF on an exchange.",
    definition:
      "Market makers provide two-sided quotes — a price at which they are willing to buy and a price at which they are willing to sell — throughout the trading day. By doing so, they help ensure that investors can generally buy or sell shares at any time without needing a specific counterparty. Competition among market makers tends to narrow bid-ask spreads and improve price discovery over time.",
    categories: ["Trading"],
    index: "#020",
    related: [
      { label: "Lead Market Maker", id: "term-lead-market-maker" },
      { label: "Bid-Ask Spread", id: "term-bid-ask-spread" },
      { label: "Liquidity", id: "term-liquidity" },
    ],
  },
  {
    term: "Market Price",
    headline:
      "The price at which an ETF share is bought or sold on an exchange during the trading day.",
    definition:
      "Unlike mutual funds, which transact at NAV calculated after market close, ETFs trade continuously on exchanges. The market price is set by the interaction of buyers and sellers and can be slightly above or below NAV depending on supply, demand, and trading conditions. For liquid ETFs, market price typically stays very close to NAV, with any meaningful deviation usually corrected quickly through the arbitrage actions of Authorized Participants and market makers.",
    categories: ["Trading", "Basics"],
    index: "#021",
    related: [
      { label: "NAV", id: "term-net-asset-value" },
      { label: "Premium to NAV", id: "term-premium-to-nav" },
      { label: "Discount to NAV", id: "term-discount-to-nav" },
    ],
  },
  // N
  {
    term: "Nasdaq-100",
    headline:
      "A stock market index tracking 100 of the largest non-financial companies listed on The Nasdaq Stock Market.",
    definition:
      "The Nasdaq-100° is a stock market index tracking the stock performance of 100 of the largest domestic and international non-financial companies listed on The Nasdaq Stock. The Nasdaq-100° Price Return Index (QQQ™) is an exchange-traded unit investment trust that invests in as many of the stocks in the Nasdaq-100° Index as is practicable. The Fund is not affiliated with sponsored, endorsed, sold or promoted by Invesco QQQ Trust (QQQ), Nasdaq™ or their affiliates. An investment cannot be made directly in an index or average. All indexes and averages are unmanaged.",
    categories: ["Basics"],
    index: "#048",
    related: [
      { label: "S&P 500 Index", id: "term-sp-500-index" },
      { label: "Reference Asset", id: "term-reference-asset" },
      { label: "QQQ Index Reference Price", id: "term-qqq-index-reference-price" },
    ],
  },
  {
    term: "Net Asset Value",
    headline: "The per-share value of an ETF’s assets minus its liabilities.",
    definition:
      "Net Asset Value, or NAV, is calculated by subtracting a fund’s liabilities from its total assets and dividing that amount by the number of shares outstanding. NAV is typically calculated once each business day. Because ETFs trade on an exchange throughout the trading day, market price may differ from NAV.",
    categories: ["Basics"],
    index: "#022",
    formula: "NAV per Share = (Total Assets − Total Liabilities) ÷ Shares Outstanding",
    formulaLabel: "Formula",
    related: [
      { label: "iNAV", id: "term-intraday-nav" },
      { label: "Premium", id: "term-premium-to-nav" },
      { label: "Discount", id: "term-discount-to-nav" },
    ],
  },
  // O
  {
    term: "Outcome Period",
    headline: "The defined window over which a buffer ETF’s protection applies.",
    definition:
      "Buffer ETFs are structured around a specific outcome period — typically one year — during which the buffer and cap are fixed. Investors who buy mid-period receive a different effective buffer and cap based on elapsed time and market movement. At period end, the fund resets with new parameters set by the options market.",
    categories: ["Structure"],
    index: "#023",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Cap Rate", id: "term-cap-rate" },
    ],
  },
  // P
  {
    term: "Premium to NAV",
    headline: "When an ETF’s market price is higher than its net asset value.",
    definition:
      "A premium occurs when an ETF’s market price is above its NAV. Premiums may occur for a variety of reasons, including market volatility, trading conditions, or differences between the trading hours of the ETF and its underlying holdings.",
    categories: ["Trading", "Basics"],
    index: "#024",
    related: [
      { label: "Discount to NAV", id: "term-discount-to-nav" },
      { label: "NAV", id: "term-net-asset-value" },
    ],
  },
  {
    term: "Premium / Discount History",
    headline: "A historical record of the difference between an ETF’s market price and NAV.",
    definition:
      "Premium/discount history shows how an ETF’s market price has compared to its NAV over time. It is typically presented using end-of-day data and may help illustrate how closely market price has tracked NAV.",
    categories: ["Trading", "Basics"],
    index: "#025",
  },
  {
    term: "Participation Rate",
    headline:
      "The percentage of an underlying asset’s positive performance that an investor receives during an outcome period.",
    definition:
      "In structured and defined-outcome strategies, a participation rate defines how much of the reference asset’s upside the investor captures. A 100% participation rate means the investor receives the full positive return (up to any applicable cap). A rate below 100% means only a portion of gains is captured. Participation rates are typically set at the start of an outcome period and are influenced by current options pricing, the size of any buffer, and market conditions.",
    categories: ["Structure"],
    index: "#026",
    related: [
      { label: "Upside Cap", id: "term-upside-cap" },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Path Dependency",
    headline:
      "The phenomenon where returns depend not just on start and end values, but on the route traveled in between.",
    definition:
      "For most buy-and-hold investments, the path of prices matters less than the beginning and ending values. But for products with daily leverage resets, options overlays, or other dynamic features, the sequence and magnitude of intermediate price moves can have a significant impact on outcomes. In volatile, range-bound markets, path dependency can substantially reduce returns even when the reference asset ends near where it started. Understanding this concept is important for any investor using daily-reset or options-based strategies.",
    categories: ["Advanced", "Risk"],
    index: "#027",
    related: [
      { label: "Daily Reset", id: "term-daily-reset" },
      { label: "Compounding", id: "term-compounding" },
    ],
  },
  {
    term: "Period Return",
    headline: "The fund’s return from the start of the current outcome period.",
    definition: "Will be 0 as it is always at the start of the period.",
    categories: ["Structure", "Basics"],
    index: "#049",
    related: [
      { label: "Outcome Period", id: "term-outcome-period" },
      { label: "Total Return", id: "term-total-return" },
    ],
  },
  // Q
  {
    term: "QQQ Index Reference Price",
    headline: "The Nasdaq-100 Index level used to set a fund’s structured outcomes.",
    definition:
      "The Nasdaq-100 Index that we will set the structured outcomes on. This is the price that the buffer starts on.",
    categories: ["Structure"],
    index: "#062",
    related: [
      { label: "Nasdaq-100", id: "term-nasdaq-100" },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  // R
  {
    term: "Rebalancing",
    headline: "The periodic process of realigning holdings to match the target index.",
    definition:
      "Indices reconstitute on scheduled dates — adding securities, removing others, adjusting weights. The ETF must then trade to match, generating transaction costs and potential capital gains. How an ETF manages rebalancing efficiency is a measure of its operational quality.",
    categories: ["Structure", "Basics"],
    index: "#028",
  },
  {
    term: "Reconstitution",
    headline:
      "The process of changing the actual holdings of an index based on updated eligibility criteria.",
    definition:
      "Reconstitution is distinct from rebalancing: while rebalancing adjusts the weights of existing holdings, reconstitution changes the composition of the index itself — adding newly eligible securities and removing those that no longer qualify. Reconstitution events can generate significant trading activity as funds tracking the index must buy new entrants and sell removals. This creates predictable demand signals that can temporarily affect the prices of securities flowing in and out of major indices.",
    categories: ["Structure", "Basics"],
    index: "#029",
    related: [{ label: "Rebalancing", id: "term-rebalancing" }],
  },
  {
    term: "Reference Asset",
    headline: "The underlying market index that the ETF tracks.",
    definition: "The underlying market index that the ETF tracks.",
    categories: ["Basics", "Structure"],
    index: "#050",
    related: [
      { label: "S&P 500 Index", id: "term-sp-500-index" },
      { label: "Nasdaq-100", id: "term-nasdaq-100" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Remaining Buffer",
    headline:
      "The amount of downside protection remaining from the current point to the end of the outcome period.",
    definition:
      "Remaining buffer reflects how much of the fund’s stated buffer is still available to absorb future losses in the reference asset. If the reference asset has not declined during the outcome period, the remaining buffer is generally equal to the starting buffer. As the reference asset declines and the buffer absorbs losses, the remaining buffer decreases. An investor purchasing mid-period should evaluate remaining buffer to understand the protection available for the rest of the current outcome period.",
    categories: ["Structure", "Risk"],
    index: "#043",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
      { label: "Downside Before Buffer", id: "term-downside-before-buffer" },
    ],
  },
  {
    term: "Remaining Cap",
    headline:
      "The maximum additional return the fund can achieve from the current point to the end of the outcome period.",
    definition:
      "Remaining cap represents the upside potential still available within the current outcome period. As the fund’s return increases toward the starting cap, the remaining cap decreases. An investor purchasing mid-period should evaluate remaining cap to understand the maximum upside available for the remainder of the outcome period. Once the fund’s return reaches the starting cap, remaining cap is effectively zero.",
    categories: ["Structure", "Risk"],
    index: "#044",
    related: [
      { label: "Cap Rate", id: "term-cap-rate" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Remaining Outcome Period",
    headline: "The time remaining until the current outcome period expires.",
    definition: "How long until the option structure expires.",
    categories: ["Structure"],
    index: "#051",
    related: [
      { label: "Outcome Period", id: "term-outcome-period" },
      { label: "Days Remaining", id: "term-days-remaining" },
      { label: "Buffer", id: "term-buffer" },
    ],
  },
  // S
  {
    term: "Short-Term Capital Gain",
    headline: "Profit from selling investments held for one year or less.",
    definition:
      "Short-term capital gains are profits from selling investments held for one year or less, passed on to shareholders.",
    categories: ["Income"],
    index: "#077",
    related: [{ label: "Long-Term Capital Gain", id: "term-long-term-capital-gain" }],
  },
  {
    term: "30-Day SEC Yield",
    headline: "A standardized yield calculation that reflects net investment income.",
    definition:
      "The 30-Day SEC Yield is a standardized yield measure calculated under SEC rules. It is based on a fund’s net investment income over the prior 30 days, annualized, and net of expenses. Because the calculation is standardized, it can be used to compare funds on a consistent basis.",
    categories: ["Income"],
    index: "#030",
    formula:
      "SEC Yield = 2 × [(net income / NAV + 1)^6 − 1] · Calculated on the last day of each month · Net of expense ratio",
    formulaLabel: "SEC Formula (simplified)",
    related: [
      { label: "Distribution Yield", id: "term-distribution-yield" },
      { label: "Yield to Worst", id: "term-yield-to-worst" },
    ],
  },
  {
    term: "S&P 500 Index",
    headline: "A stock market index tracking 500 of the largest US-listed companies.",
    definition:
      "The S&P 500 Index is a stock market index tracking the stock performance of 500 of the largest companies listed on stock exchanges in the United States.",
    categories: ["Basics"],
    index: "#052",
    related: [
      { label: "SPDR® S&P 500 ETF Trust (SPY)", id: "term-spdr-sp-500-etf-trust" },
      { label: "SPX Index Reference Price", id: "term-spx-index-reference-price" },
      { label: "Reference Asset", id: "term-reference-asset" },
    ],
  },
  {
    term: "S&P Downside to Floor of Buffer",
    headline:
      "How far the S&P 500 must fall from its current level before the downside buffer ends.",
    definition:
      "How far the S&P 500 would need to fall from its current level before the downside buffer ends. A value of N/A is shown if the S&P 500 Price Index falls below the buffer. Note that this is not a reflection of how much of the buffer has been used, only how far the S&P is away from the lowest level of the buffer. To understand how much buffer remains, look at “Remaining Buffer”.",
    categories: ["Structure", "Risk"],
    index: "#053",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Remaining Buffer", id: "term-remaining-buffer" },
      { label: "Downside Before Buffer", id: "term-downside-before-buffer" },
    ],
  },
  {
    term: "SPDR® S&P 500 ETF Trust (SPY)",
    headline: "An ETF providing cost-efficient, highly liquid exposure to the S&P 500® Index.",
    definition:
      "The SPDR® S&P 500* ETF Trust (SPY) gives investors cost-efficient, highly liquid exposure to the S&P 500® Index—providing access to 500 of the largest publicly traded US firms. The Fund is not affiliated with sponsored, endorsed, sold or promoted by SPDR®. An investment cannot be made directly in an index or average. All indexes and averages are unmanaged.",
    categories: ["Basics"],
    index: "#054",
    related: [
      { label: "S&P 500 Index", id: "term-sp-500-index" },
      { label: "SPX Index Reference Price", id: "term-spx-index-reference-price" },
      { label: "SPY Index Reference Price", id: "term-spy-index-reference-price" },
    ],
  },
  {
    term: "SPX Index Reference Price",
    headline: "The S&P 500 Price Index level used to set a fund’s structured outcomes.",
    definition:
      "The S&P 500 Price Index that we will set the structured outcomes on. This is the price that the buffer starts on.",
    categories: ["Structure"],
    index: "#055",
    related: [
      { label: "S&P 500 Index", id: "term-sp-500-index" },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "SPY Index Reference Price",
    headline: "The S&P 500 Index level used to set a fund’s structured outcomes.",
    definition:
      "The S&P 500 Index that we will set the structured outcomes on. This is the price that the buffer starts on.",
    categories: ["Structure"],
    index: "#063",
    related: [
      { label: "SPDR® S&P 500 ETF Trust (SPY)", id: "term-spdr-sp-500-etf-trust" },
      { label: "S&P 500 Index", id: "term-sp-500-index" },
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
    ],
  },
  {
    term: "Sharpe Ratio",
    headline:
      "A measure of risk-adjusted return: how much excess return an investment has generated per unit of volatility.",
    definition:
      "The Sharpe Ratio compares an investment’s return above a risk-free rate to its standard deviation (volatility). In general, a higher ratio suggests more return per unit of risk taken. It is most useful as a comparative tool when evaluating strategies with similar investment objectives over the same time period. A high Sharpe Ratio in one market environment may not persist — it should always be considered alongside other risk and return metrics.",
    categories: ["Risk"],
    index: "#031",
    formula: "Sharpe Ratio = (Portfolio Return − Risk-Free Rate) ÷ Portfolio Standard Deviation",
    formulaLabel: "Concept",
    related: [{ label: "Volatility", id: "term-volatility" }],
  },
  {
    term: "Special Purpose Vehicle",
    headline: "A separate legal entity created to isolate financial risk or hold specific assets.",
    definition:
      "A Special Purpose Vehicle, or SPV, is a legally separate entity created for a narrow, defined purpose. In the ETF context, SPVs can be used to warehouse illiquid or hard-to-hold assets — such as shares of a private company, direct lending positions, or bespoke derivatives — that don’t fit neatly into a standard fund wrapper, allowing the ETF to gain exposure without directly holding the underlying on its balance sheet.",
    categories: ["Structure", "Advanced"],
    abbreviation: "SPV",
    index: "#041",
    related: [
      { label: "FLEX Options", id: "term-flex-options" },
      { label: "Authorized Participant", id: "term-authorized-participant" },
    ],
  },
  // T
  {
    term: "Total Distribution",
    headline: "The total amount paid per share on a distribution date.",
    definition:
      "The total amount paid per share on this date, combining income and any capital gains.",
    categories: ["Income"],
    index: "#078",
    related: [
      { label: "Distribution NAV", id: "term-distribution-nav" },
      { label: "Dividend Income", id: "term-dividend-income" },
    ],
  },
  {
    term: "Tracking Error",
    headline: "A statistical measure of how closely an ETF follows its benchmark index.",
    definition:
      "Tracking error measures the variability of the difference between an ETF’s returns and the returns of its benchmark index over time. It is commonly expressed as the standard deviation of return differences. Tracking difference refers to the difference between the ETF’s return and the benchmark’s return over a stated period.",
    categories: ["Risk", "Basics"],
    index: "#032",
  },
  {
    term: "Total Return",
    headline: "The combined return of price changes and reinvested distributions.",
    definition:
      "Total return measures performance by taking into account changes in share price or NAV, together with the effect of distributions, assuming reinvestment unless otherwise stated.",
    categories: ["Basics"],
    index: "#033",
  },
  {
    term: "Total Return Swap",
    headline:
      "A derivative contract in which one party pays the total return of a reference asset to a counterparty in exchange for a financing rate, used by leveraged ETFs to achieve daily magnified exposure.",
    definition:
      "A derivative contract in which one party pays the total return of a reference asset to a counterparty in exchange for a financing rate, used by leveraged ETFs to achieve daily magnified exposure.",
    categories: ["Structure"],
    index: "#084",
  },
  {
    term: "Tracking Difference",
    headline:
      "The cumulative return gap between an ETF and its reference index over a given period.",
    definition:
      "Tracking difference is the total return of the ETF minus the total return of its reference index over a specified period. It captures the net effect of fees, trading costs, cash drag, securities lending income, tax treatment, and portfolio management decisions. Unlike tracking error — which measures consistency — tracking difference measures magnitude. A fund can have low tracking error but still meaningfully underperform its index over time. Both metrics are worth examining when evaluating an index fund or ETF.",
    categories: ["Basics", "Risk"],
    index: "#034",
    related: [
      { label: "Tracking Error", id: "term-tracking-error" },
      { label: "Expense Ratio", id: "term-expense-ratio" },
    ],
  },
  {
    term: "Treasury Bill (T-Bill)",
    headline:
      "A short-term U.S. government debt security that matures in one year or less, sold at a discount and redeemed at face value.",
    definition:
      "A short-term U.S. government debt security that matures in one year or less, sold at a discount and redeemed at face value.",
    categories: ["Basics"],
    index: "#082",
  },
  {
    term: "Treasury Bond",
    headline:
      "A U.S. government debt security that pays a fixed coupon and matures in more than one year, backed by the full faith and credit of the U.S. Treasury.",
    definition:
      "A U.S. government debt security that pays a fixed coupon and matures in more than one year, backed by the full faith and credit of the U.S. Treasury.",
    categories: ["Basics"],
    index: "#083",
  },
  // U
  {
    term: "Upside Cap",
    headline: "The maximum positive return an investor can receive during a stated outcome period.",
    definition:
      "In buffer and defined-outcome strategies, an upside cap limits the gain an investor receives if the underlying asset appreciates significantly. If the reference asset rises by more than the cap, the investor receives the cap return, not the full gain. The cap exists because the strategy sells call options to fund the purchase of put options that provide downside protection. Caps are determined at the start of each outcome period and fluctuate based on options pricing, volatility, and the size of the buffer.",
    categories: ["Structure"],
    index: "#035",
    related: [
      { label: "Buffer", id: "term-buffer" },
      { label: "Outcome Period", id: "term-outcome-period" },
      { label: "Participation Rate", id: "term-participation-rate" },
      { label: "FLEX Options", id: "term-flex-options" },
    ],
  },
  {
    term: "US Small Cap",
    headline:
      "Publicly traded U.S. companies with relatively small market capitalizations, typically tracked by the Russell 2000 Index.",
    definition:
      "US small-cap stocks are shares of companies with market capitalizations generally ranging from roughly $300 million to $2 billion. The Russell 2000® Index is the most widely used benchmark for the U.S. small-cap segment, comprising the smallest 2,000 companies in the Russell 3000® Index. Small-cap companies tend to be more domestically focused, earlier in their growth trajectory, and more sensitive to U.S. economic conditions than their large-cap counterparts. While small-cap equities have historically offered higher long-term return potential, they also exhibit greater volatility, lower liquidity, and wider bid-ask spreads. Sector composition differs meaningfully from large-cap indexes, with greater weight in financials, industrials, and healthcare relative to the technology-heavy S&P 500.",
    categories: ["Basics"],
    index: "#085",
    related: [
      { label: "iShares Russell 2000 (IWM)", id: "term-ishares-russell-2000" },
      { label: "S&P 500 Index", id: "term-sp-500-index" },
      { label: "Volatility", id: "term-volatility" },
    ],
  },
  // V
  {
    term: "Volatility",
    headline: "The statistical measure of how widely returns are dispersed.",
    definition:
      "Annualized volatility — the standard deviation of daily returns scaled to a year — quantifies return variability. Higher volatility = larger swings in both directions. Volatility is neither inherently good nor bad; what matters is volatility relative to expected return (see: Sharpe ratio) and to the investor’s time horizon.",
    categories: ["Risk"],
    index: "#036",
  },
  // W
  {
    term: "Weighted Avg. Maturity",
    headline: "The average time until the bonds in a fund are repaid, weighted by position size.",
    definition:
      "WAM is not the same as duration — duration accounts for coupon payments and time value; WAM does not. Two funds with the same WAM can have very different interest rate sensitivity depending on their coupon structures.",
    categories: ["Income"],
    index: "#037",
  },
  // Y
  {
    term: "Yield to Maturity",
    headline: "The total annual return if a fund’s bonds are held to maturity.",
    definition:
      "Yield to maturity is the total yearly return expected if the fund's bonds are held until they mature and all scheduled payments are made.",
    categories: ["Income"],
    index: "#079",
    related: [
      { label: "Yield to Worst", id: "term-yield-to-worst" },
      { label: "SEC Yield", id: "term-30-day-sec-yield" },
    ],
  },
  {
    term: "Yield to Worst",
    headline: "A calculation of the lowest potential yield a bond can generate without defaulting.",
    definition:
      "Yield to worst, or YTW, is the lowest yield a bond or bond portfolio can produce assuming the issuer exercises any available call, prepayment, or similar option at the earliest permitted date, without defaulting. It is commonly used as a conservative yield measure for callable fixed-income securities.",
    categories: ["Income", "Risk"],
    index: "#038",
    related: [
      { label: "30-Day SEC Yield", id: "term-30-day-sec-yield" },
      { label: "Duration", id: "term-duration" },
    ],
  },
];
